import Badge from '@pelckmans/elementis/components/badge';
import Navigation from '@pelckmans/elementis/components/navigation';
import LessonsIcon from '@pelckmans/elementis/icons/lessons';
import MaterialMediaIcon from '@pelckmans/elementis/icons/material-media';
import ResultsIcon from '@pelckmans/elementis/icons/results';
import SearchIcon from '@pelckmans/elementis/icons/search';
import TaskIcon from '@pelckmans/elementis/icons/tasks';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ValuesOfObject } from '../../../../@types/utils';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import MyAssignmentsContext from '../../../../contexts/MyAssignmentsContext';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useRoutes } from '../../../../hooks/useRoutes';
import ModuleContext from '../../../../pages/material/_contexts/ModuleContext';
import useModuleAccess from '../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../core/access/module-access';

interface Props {
  iconOnly?: boolean;
  onClick?: () => void;
}

function MethodNavigation({ iconOnly = true, onClick }: Props) {
  const { module: superModule } = useContext(ModuleContext);
  const { getIncompleteAssignmentCountByModule } = useContext(MyAssignmentsContext);
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { buildAbsolutePath, getRoute } = useRoutes();
  const { assignmentsAccess, resultsAccess } = useModuleAccess(superModule);

  if (!superModule) return;

  const params = {
    subjectSlug: superModule?.subjectSlug || '',
    methodSlug: superModule?.methodSlug || '',
    moduleSlug: superModule?.slug || '',
  };

  const moduleAssignmentsCount = getIncompleteAssignmentCountByModule(superModule);

  const handleClick = (eventType?: ValuesOfObject<typeof ANALYTICS_EVENT_TYPES>) => {
    if (eventType) {
      analytics.capture({
        eventType,
        objectId: superModule?.id,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SUPERMODULE_NAVIGATION,
      });
    }

    if (onClick) {
      onClick();
    }
  };

  return (
    <Navigation aria-label={t('APP_NAV.SIDE_NAV.MATERIAL_NAVIGATION.A11Y_LABEL')}>
      <Navigation.List orientation="vertical">
        <Navigation.Item
          icon={<LessonsIcon />}
          iconOnly={iconOnly}
          to={buildAbsolutePath(['MATERIAL'], params)}
          end={!window.location.pathname.includes(getRoute('MATERIAL.CONTENT'))}
          onClick={() => handleClick()}
        >
          {t('APP_NAV.SIDE_NAV.MODULES')}
        </Navigation.Item>
        <Navigation.Item
          onClick={() => handleClick(ANALYTICS_EVENT_TYPES.CATEGORICAL_MATERIAL_ACCESSED)}
          icon={<MaterialMediaIcon />}
          iconOnly={iconOnly}
          to={buildAbsolutePath(['MATERIAL', 'MATERIAL.CATEGORICAL'], params)}
          end={false}
        >
          {t('APP_NAV.SIDE_NAV.CATEGORICAL_MATERIAL')}
        </Navigation.Item>
        {assignmentsAccess !== FEATURE_ACCESS.HIDDEN && (
          <Navigation.Item
            onClick={() => handleClick(ANALYTICS_EVENT_TYPES.ASSIGNMENTS_ACCESSED)}
            badge={moduleAssignmentsCount ? <Badge>{moduleAssignmentsCount}</Badge> : undefined}
            icon={<TaskIcon />}
            iconOnly={iconOnly}
            to={buildAbsolutePath(['MATERIAL', 'MATERIAL.ASSIGNMENTS'], params)}
            end={false}
          >
            {t('APP_NAV.SIDE_NAV.ASSIGNMENTS')}
          </Navigation.Item>
        )}
        {resultsAccess !== FEATURE_ACCESS.HIDDEN && (
          <Navigation.Item
            onClick={() => handleClick(ANALYTICS_EVENT_TYPES.RESULTS_ACCESSED)}
            icon={<ResultsIcon />}
            iconOnly={iconOnly}
            to={buildAbsolutePath(['MATERIAL', 'MATERIAL.RESULTS'], params)}
            end={false}
          >
            {t('APP_NAV.SIDE_NAV.RESULTS')}
          </Navigation.Item>
        )}
        <Navigation.Item
          icon={<SearchIcon />}
          iconOnly={iconOnly}
          to={buildAbsolutePath(['MATERIAL', 'MATERIAL.SEARCH'], params)}
          onClick={() => handleClick(ANALYTICS_EVENT_TYPES.SEARCH_ACCESSED)}
        >
          {t('APP_NAV.SIDE_NAV.SEARCH')}
        </Navigation.Item>
      </Navigation.List>
    </Navigation>
  );
}

export default MethodNavigation;
